import type { FieldKey, FieldOf, FieldTypeName, RowData } from '@/api/types'
import type { RowComparator, SortDirection } from '../compare'

/** How a field type answers the `contains` family of filters. */
export type ContainsKind = 'text' | 'filename' | 'none'

/**
 * Everything the application needs to know about a field type that does not
 * involve rendering.
 *
 * Pure by construction: no React, no network, no store. That is what lets the
 * MCP apps import it, lets the row window engine decide whether a changed row
 * still belongs in a view without mounting anything, and lets all of it be
 * tested against plain data.
 *
 * The registry it replaces was runtime-mutable because upstream Baserow lets
 * third-party plugins add field types. Nothing does that here, so this is a
 * static map instead -- which buys exhaustiveness: a twenty-eighth field type
 * fails to compile until every one of these is answered for it.
 */
export interface FieldBehavior<T extends FieldTypeName = FieldTypeName> {
  readonly type: T

  /**
   * Whether a cell counts as having no value.
   *
   * Not the same as falsy. A rating of zero and an unticked checkbox are both
   * empty, an empty string in a text field is empty, and a link row cell holding
   * only unsaved rows is empty -- each of which a plain falsy check gets wrong in
   * one direction or the other.
   */
  isEmpty: (field: FieldOf<T>, value: unknown) => boolean

  /**
   * Whether two cells hold the same value, for deciding what shares a group.
   *
   * Compares row values, so a group value has to be converted to one first.
   */
  isEqual: (field: FieldOf<T>, a: unknown, b: unknown) => boolean

  /**
   * Orders rows by this field, or null when the type cannot be sorted on.
   *
   * Null is a real answer -- a file or password field has no order -- and is
   * distinct from a comparator that returns 0 for everything, which would let a
   * view be sorted by something that then does nothing.
   */
  getSort: (
    field: FieldOf<T>,
    key: FieldKey,
    direction: SortDirection,
    context?: FieldContext,
  ) => RowComparator | null

  /**
   * Renders a cell as the text the grid shows for it.
   *
   * This is what the `contains` family of filters matches against, so it has to
   * be what somebody looking at the view would read -- matching the raw stored
   * value instead would make a filter agree with the database and disagree with
   * the person who typed it.
   *
   * An absent value renders as the empty string rather than as a placeholder,
   * which would itself be matchable.
   */
  toHumanReadable: (
    field: FieldOf<T>,
    value: unknown,
    context?: FieldContext,
    delimiter?: string,
  ) => string

  /**
   * How the `contains` family matches this field type, if at all.
   *
   * `'text'` matches against the cell's human-readable text. `'filename'` reads
   * an attachment cell's files directly instead, so that the commas joining
   * their names cannot be matched and a filter value cannot span the end of one
   * name and the start of the next. `'none'` means the type has no `contains`
   * behaviour and never matches -- a duration or a link row, say.
   *
   * A function rather than a constant because a formula answers for the type its
   * result behaves like, which is a property of the field, not of its type.
   */
  containsKind: (field: FieldOf<T>) => ContainsKind

  /**
   * Whether the `contains word` family matches this field type.
   *
   * A strictly smaller set than `containsKind`: a number renders as text and so
   * can be substring-matched, but "whole word" has no meaning for it, and
   * upstream returns false rather than pretending otherwise.
   */
  containsWordKind: (field: FieldOf<T>) => ContainsKind

  /**
   * Reads a value out of the text a linked row carries for its primary field.
   *
   * A link row cell only holds the primary field's rendered text, so sorting a
   * table by a link row means parsing that text back into whatever the related
   * field's own comparator expects.
   */
  parseFromLinkedRowItemValue: (field: FieldOf<T>, value: string) => unknown
}

/**
 * The lookups a field operation needs that are not on the row.
 *
 * Collaborator and last-modified-by fields store a user id and the name that was
 * current when the row was written. The workspace's member list is the
 * authority, and it is server state -- so it is passed in rather than read from
 * a store, which is what keeps this layer pure.
 *
 * Named for the field rather than for sorting because rendering wants the same
 * lookup: a stale name must not be what a `contains` filter matches against.
 */
export interface FieldContext {
  /** The member's current name, or undefined when they are not resolvable. */
  resolveUserName?: (userId: number) => string | undefined
  /**
   * Everybody a collaborator cell may name: the workspace's members.
   *
   * The list rather than a lookup, because the editor for that field type has
   * to *offer* them and a paste has to find one by name. Same argument as the
   * lookup above -- it is server state, so it is passed in rather than read
   * from a store, which is what keeps this layer pure.
   */
  collaborators?: readonly { id: number; name: string }[]
}

/**
 * The default rendering: the value itself, or nothing.
 *
 * `value || ''` upstream, so a zero or a `false` renders as the empty string --
 * wrong read literally, but right in effect for the types that fall through to
 * here, none of which store either.
 *
 * Restricted to the scalars those types actually hold. Upstream returns
 * whatever it was given, including an object, and leaves the caller to
 * stringify it into "[object Object]" -- fifteen characters a `contains` filter
 * can then match.
 */
export function toHumanReadableByDefault(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value ? String(value) : ''
  }
  return ''
}

/** A cell value that names a user, as collaborator-shaped fields store them. */
export interface UserValue {
  id: number
  name?: string
}

/** A cell value that names a select option. */
export interface SelectOptionValue {
  id?: number
  value?: string
  color?: string
}

/** One row referenced by a link row cell. */
export interface LinkedRowValue {
  id: number
  value?: string
  order?: string
}

// ---------------------------------------------------------------------------
// Shared implementations
// ---------------------------------------------------------------------------

/**
 * Whether a value is empty by the rule that covers most field types.
 *
 * A blank string, an array of nothing, an array whose entries are all blank, an
 * object with no keys, and `false` all count. `false` is deliberate: an unticked
 * checkbox reads as an empty cell in a filter, which is what somebody looking at
 * the view means by it.
 */
export function isEmptyByDefault(value: unknown): boolean {
  const isBlank = (v: unknown): boolean => {
    if (v === null || v === undefined) return true
    if (Array.isArray(v)) return v.length === 0
    // An object with anything in it is a value; the empty-object case is handled
    // below, where it can be recognised as such rather than stringified.
    if (typeof v === 'string') return v.trim() === ''
    // Anything left -- a number, a boolean, an object -- is a value. Upstream
    // stringified first, which reaches the same answer by a longer road: `0`,
    // `false` and `[object Object]` are all non-blank.
    return false
  }

  if (Array.isArray(value)) {
    const entries = value as unknown[]
    if (
      entries.length === 0 ||
      entries.every((entry) => {
        const record = entry as Record<string, unknown> | null
        return isBlank(record?.['value'] ?? record?.['name'])
      })
    ) {
      return true
    }
  }

  if (value !== null && typeof value === 'object' && Object.keys(value).length === 0) {
    return true
  }

  return value === false || isBlank(value)
}

/**
 * Whether two values are the same, by serialising both.
 *
 * Crude but correct for these shapes, which are JSON the backend sent: the key
 * order is the serializer's, and therefore the same for two values of one field.
 */
export function isEqualByDefault(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** The name to sort a user by: the workspace's, falling back to the row's. */
export function userSortName(value: unknown, context?: FieldContext): string {
  if (value === null || value === undefined) return ''
  const user = value as UserValue
  return context?.resolveUserName?.(user.id) ?? user.name ?? ''
}

/** Reads a row's cell, treating a missing one as null rather than undefined. */
export function cell(row: RowData, key: FieldKey): unknown {
  return row[key] ?? null
}
